import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { useShift } from '@/hooks/useShift';
import { usePatientRole } from '@/hooks/usePatientRole';
import { sanitizePatientData } from '@/hooks/useSensitivePatientData';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useTaskAlarms } from '@/hooks/useTaskAlarms';
import { TaskAlarmOverlay } from '@/components/caregiver/TaskAlarmOverlay';
import { CoachMark } from '@/components/onboarding/CoachMark';

import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
// CheckInOutModal removed
import { PatientDetailsCard } from '@/components/patient/PatientDetailsCard';
import { PullToRefresh } from '@/components/ui/pull-to-refresh';
import { TaskFeedItem } from '@/components/dashboard/TaskFeedItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  LogIn,
  LogOut,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertCircle,
  Pill,
  Activity,
  Droplets,
  Utensils,
  ChevronRight,
  ShieldX,
  AlertTriangle,
  Plus,
  Package,
  Moon,
  Sun,
  Dumbbell,
  Loader2
} from 'lucide-react';
import { format, parseISO, isToday, isYesterday, startOfDay } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import type { TaskInstance } from '@/types/caregiver';
import type { ShiftTemplate } from '@/types/shift-templates';
import { getShiftHoursForDay, isTimeInShift } from '@/types/shift-templates';
import { cn } from '@/lib/utils';
import i18n from '@/i18n';

// Agenda Task Item Component with highlight support and shift indicator
interface AgendaTaskItemProps {
  instance: {
    id: string;
    isCompleted: boolean;
    isPast: boolean;
    execution_id?: string | null;
  };
  template: {
    id?: string;
    name?: string;
    task_type?: string;
    dose?: string;
    dose_unit?: string;
  } | null;
  scheduledTime: string;
  isHighlighted: boolean;
  isCheckedInForThisPatient: boolean;
  isInShift: boolean;
  hasShiftTemplate: boolean;
  getTaskIcon: (type: string) => React.ReactNode;
  getTaskColor: (type: string) => string;
  onClick: () => void;
}

function AgendaTaskItem({
  instance,
  template,
  scheduledTime,
  isHighlighted,
  isCheckedInForThisPatient,
  isInShift,
  hasShiftTemplate,
  getTaskIcon,
  getTaskColor,
  onClick,
  t
}: AgendaTaskItemProps & { t: (key: string) => string }) {
  const itemRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Only scroll when explicitly highlighted (not on initial render)
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (isHighlighted && itemRef.current && !hasScrolled.current) {
      hasScrolled.current = true;
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);

  return (
    <div
      ref={itemRef}
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg transition-colors relative",
        instance.isCompleted
          ? "bg-success/10"
          : instance.isPast
            ? "bg-destructive/10"
            : isInShift
              ? "bg-muted/50"
              : "bg-muted/20 opacity-60",
        isCheckedInForThisPatient && isInShift && "cursor-pointer hover:bg-muted",
        isHighlighted && "ring-2 ring-primary ring-offset-2 ring-offset-background",
        isAnimating && "animate-pulse",
        // Left border indicator for shift status
        hasShiftTemplate && (isInShift
          ? "border-l-4 border-l-primary"
          : "border-l-4 border-l-muted-foreground/30")
      )}
      onClick={() => {
        if (isCheckedInForThisPatient && isInShift) {
          onClick();
        }
      }}
    >
      <div className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center",
        instance.isCompleted
          ? "bg-success text-success-foreground"
          : instance.isPast
            ? "bg-destructive/20 text-destructive"
            : isInShift
              ? "bg-muted"
              : "bg-muted/50"
      )}>
        {instance.isCompleted ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : instance.isPast ? (
          <AlertCircle className="w-5 h-5" />
        ) : (
          <span className={cn(
            getTaskColor(template?.task_type || ''),
            !isInShift && "opacity-50"
          )}>
            {getTaskIcon(template?.task_type || '')}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={cn(
            "font-medium truncate",
            instance.isCompleted && "text-muted-foreground",
            !isInShift && !instance.isCompleted && "text-muted-foreground"
          )}>
            {template?.name || t('taskDefault.task')}
          </p>
          {hasShiftTemplate && !isInShift && !instance.isCompleted && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[12px] px-1 py-0 h-4 bg-muted/50 text-muted-foreground border-muted-foreground/30">
                    {t('caregiver.outsideShift')}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('caregiver.taskOutsideShiftTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {scheduledTime}
          {template?.dose && ` • ${template.dose} ${template.dose_unit}`}
        </p>
      </div>

      {isCheckedInForThisPatient && isInShift && (
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      )}
    </div>
  );
}

export default function CaregiverAgendaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const openComments = searchParams.get('openComments') === 'true';
  const highlightCommentId = searchParams.get('commentId');
  const { user } = useAuth();
  const { setFocusedPatientId } = useFocusedPatient();
  const { activeShift, isLoading: shiftLoading, refreshShift } = useShift();
  const { canExecuteTasks, isCaregiver, isOwner, isLoading: roleLoading } = usePatientRole(patientId);
  const { settings: systemSettings } = useSystemSettings();

  const [patient, setPatient] = useState<any>(null);
  const [taskInstances, setTaskInstances] = useState<TaskInstance[]>([]);
  const [todayExecutions, setTodayExecutions] = useState<any[]>([]); // For KPIs (Incidents, Sporadic, etc.)
  const [feedItems, setFeedItems] = useState<any[]>([]); // For the visible Feed
  const [shiftTemplate, setShiftTemplate] = useState<ShiftTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Feed pagination state
  const FEED_PAGE_SIZE = 10;
  const [feedOffset, setFeedOffset] = useState(0);
  const [hasMoreFeed, setHasMoreFeed] = useState(true);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const feedEndRef = useRef<HTMLDivElement>(null);

  // Check if this caregiver is checked in for THIS patient
  const isCheckedInForThisPatient = activeShift?.patient_id === patientId;
  // Check if this caregiver has ANY active shift (blocks check-in for other patients)
  const hasAnyActiveShift = !!activeShift;

  useEffect(() => {
    if (patientId) {
      fetchData();
    }
  }, [patientId, activeShift?.id]);

  // Prevent browser scroll restoration when entering caregiver workspace.
  // If the page wasn't opened from a highlight/deep-link, always start at the top.
  useEffect(() => {
    if (!patientId) return;

    const hasDeepLink = !!highlightId || !!highlightCommentId || openComments;
    if (!hasDeepLink) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [patientId, highlightId, highlightCommentId, openComments]);

  const [patientActiveShift, setPatientActiveShift] = useState<any>(null);

  const fetchData = async () => {
    if (!patientId) return;

    try {
      setIsLoading(true);
      // Fetch patient and its active shift in parallel
      const [patientResult, activeShiftResult] = await Promise.all([
        supabase
          .from('patients')
          .select('*')
          .eq('id', patientId)
          .single(),
        supabase
          .from('shifts')
          .select('id, check_in_at, caregiver_id, check_in_latitude, check_in_longitude, shift_template_id')
          .eq('patient_id', patientId)
          .eq('status', 'active')
          .maybeSingle()
      ]);

      setPatient(patientResult.data);

      if (activeShiftResult.data) {
        // Fetch caregiver name for the active shift
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', activeShiftResult.data.caregiver_id)
          .maybeSingle();

        setPatientActiveShift({
          ...activeShiftResult.data,
          caregiver_name: profile?.full_name || null
        });
      } else {
        setPatientActiveShift(null);
      }

      // If there's an active shift *for the current user*, fetch task instances
      if (activeShift?.id && activeShift.patient_id === patientId) {
        // Fetch shift template if available
        if (activeShift.shift_template_id) {
          const { data: templateData } = await supabase
            .from('shift_templates')
            .select('*')
            .eq('id', activeShift.shift_template_id)
            .maybeSingle();

          if (templateData) {
            setShiftTemplate(templateData as unknown as ShiftTemplate);
          } else {
            setShiftTemplate(null);
          }
        } else {
          setShiftTemplate(null);
        }

        const { data: instancesData, error: instancesError } = await supabase
          .from('shift_task_instances')
          .select(`
            *,
            task_template:care_plan_tasks(
              id, name, task_type, vital_type, unit, dose, dose_unit, 
              administration_route, photo_required, min_alert_value, 
              max_alert_value, food_type, hygiene_type, description
            )
          `)
          .eq('shift_id', activeShift.id)
          .order('scheduled_for', { ascending: true });

        if (instancesError) {
          console.error('Error fetching task instances:', instancesError);
        } else {
          setTaskInstances((instancesData as unknown as TaskInstance[]) || []);
        }
      } else {
        setTaskInstances([]);
      }

      // Fetch today's executions (for feed and stats)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { data: execData, error: execError } = await supabase
        .from('task_executions')
        .select('*')
        .eq('patient_id', patientId)
        .gte('executed_at', today.toISOString())
        .order('executed_at', { ascending: false });

      if (execError) throw execError;

      setTodayExecutions(execData || []);

      // Initialize Feed (First Page)
      await loadFeed(0, true);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const feedLoadingRef = useRef(false);

  const loadFeed = async (offset: number, reset: boolean = false) => {
    if (!patientId) return;
    // Guard against concurrent loads (intersection observer can fire multiple
    // times before `isFeedLoading` state settles), which duplicated feed cards.
    if (feedLoadingRef.current) return;
    feedLoadingRef.current = true;
    setIsFeedLoading(true);


    try {
      const { data: executions } = await supabase
        .from('task_executions')
        .select(`
          id,
          executed_at,
          is_alert,
          is_late,
          notes,
          photo_url,
          vital_value,
          vital_value_secondary,
          food_acceptance,
          is_sporadic,
          task_id,
          executed_by,
          adhoc_task_type,
          adhoc_name,
          adhoc_vital_type,
          patient_id
        `)
        .eq('patient_id', patientId)
        .order('executed_at', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + FEED_PAGE_SIZE - 1);


      if (!executions || executions.length === 0) {
        if (reset) setFeedItems([]);
        setHasMoreFeed(false);
        feedLoadingRef.current = false;
        setIsFeedLoading(false);
        return;
      }


      // Get task and profile info for batch
      const taskIds = new Set<string>();
      const executorIds = new Set<string>();
      executions.forEach(e => {
        if (e.task_id) taskIds.add(e.task_id);
        if (e.executed_by) executorIds.add(e.executed_by);
      });

      const [tasksResult, profilesResult, reactionsResult, commentsResult] = await Promise.all([
        taskIds.size > 0 ? supabase
          .from('care_plan_tasks')
          .select('id, name, task_type, vital_type, unit, incident_type')
          .in('id', Array.from(taskIds)) : { data: [] },
        executorIds.size > 0 ? supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url')
          .in('user_id', Array.from(executorIds)) : { data: [] },
        supabase
          .from('task_reactions')
          .select('task_execution_id, id, emoji, user_id')
          .in('task_execution_id', executions.map(e => e.id)),
        supabase
          .from('task_comments')
          .select('task_execution_id')
          .in('task_execution_id', executions.map(e => e.id))
      ]);

      const tasksMap = new Map((tasksResult.data || []).map(t => [t.id, t]));
      const profilesMap = new Map((profilesResult.data || []).map(p => [p.user_id, p]));

      // Map reactions and comments
      const reactionsCountMap = new Map<string, number>();
      const commentsCountMap = new Map<string, number>();
      const reactionsMap = new Map<string, Array<{ id: string; emoji: string; user_id: string }>>();

      reactionsResult.data?.forEach(r => {
        const currentCount = reactionsCountMap.get(r.task_execution_id) || 0;
        reactionsCountMap.set(r.task_execution_id, currentCount + 1);

        const currentReactions = reactionsMap.get(r.task_execution_id) || [];
        currentReactions.push({ id: r.id, emoji: r.emoji, user_id: r.user_id });
        reactionsMap.set(r.task_execution_id, currentReactions);
      });

      commentsResult.data?.forEach(c => {
        const current = commentsCountMap.get(c.task_execution_id) || 0;
        commentsCountMap.set(c.task_execution_id, current + 1);
      });

      const enriched: any[] = executions.map(e => {
        const task = e.task_id ? tasksMap.get(e.task_id) : null;
        return {
          ...e,
          task: task || {
            name: e.adhoc_name || t('taskDefault.adhocTask'),
            task_type: e.adhoc_task_type || 'other',
            incident_type: null,
            vital_type: e.adhoc_vital_type
          },
          executor: profilesMap.get(e.executed_by) || null,
          reactions_count: reactionsCountMap.get(e.id) || 0,
          comments_count: commentsCountMap.get(e.id) || 0,
          reactions: reactionsMap.get(e.id) || []
        };
      });

      if (reset) {
        setFeedItems(enriched);
        setFeedOffset(enriched.length);
      } else {
        // Dedupe by id: new executions shift the offset window, which could
        // return rows already present in the list.
        setFeedItems(prev => {
          const seen = new Set(prev.map(p => p.id));
          const merged = [...prev, ...enriched.filter(e => !seen.has(e.id))];
          setFeedOffset(merged.length);
          return merged;
        });
      }
      setHasMoreFeed(enriched.length >= FEED_PAGE_SIZE);

    } catch (error) {
      console.error('Error loading feed:', error);
    } finally {
      feedLoadingRef.current = false;
      setIsFeedLoading(false);
    }
  };


  // Wrapper for infinite scroll that uses the current offset state
  const handleLoadMore = () => {
    if (!isFeedLoading && hasMoreFeed) {
      loadFeed(feedOffset);
    }
  };

  // Keep ref updated
  const handleLoadMoreRef = useRef(handleLoadMore);
  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);

  // Intersection Observer
  useEffect(() => {
    const currentRef = feedEndRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMoreRef.current();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(currentRef);
    return () => observer.disconnect();
  }, [feedItems.length, hasMoreFeed]); // Re-attach when feed changes or ref becomes available




  // Computed values from task instances
  const now = new Date();
  const toleranceMinutes = systemSettings.late_tolerance_minutes;

  // Get current day of week for shift hours calculation
  const currentDayOfWeek = now.getDay();

  // Get shift hours for today from template
  const shiftHoursToday = useMemo(() => {
    if (!shiftTemplate) return null;
    return getShiftHoursForDay(shiftTemplate, currentDayOfWeek);
  }, [shiftTemplate, currentDayOfWeek]);

  // Helper to check if a scheduled time is within shift hours
  const checkIsInShift = (scheduledFor: string): boolean => {
    if (!shiftTemplate || !shiftHoursToday) return true; // No template = all tasks visible

    const taskTime = format(parseISO(scheduledFor), 'HH:mm');
    return isTimeInShift(
      taskTime,
      shiftHoursToday.start,
      shiftHoursToday.end,
      shiftTemplate.is_overnight
    );
  };

  // Create grouped events for feed
  const groupedEvents = useMemo(() => {
    const groups: Record<string, any[]> = {};

    feedItems.forEach(event => {
      const date = startOfDay(new Date(event.executed_at)).toISOString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });

    return Object.entries(groups).map(([date, items]) => ({
      date: new Date(date),
      items
    })).sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [feedItems]);

  const formatDateHeader = (date: Date) => {
    if (isToday(date)) return t('familyDashboard.today');
    if (isYesterday(date)) return t('familyDashboard.yesterday');
    return format(date, "EEEE, d 'de' MMMM", { locale: i18n.language === 'pt-BR' ? ptBR : undefined });
  };

  // Derive status for each instance
  const processedInstances = taskInstances.map(instance => {
    const scheduledTime = new Date(instance.scheduled_for);
    const toleranceMs = toleranceMinutes * 60 * 1000;
    const isCompleted = instance.status === 'completed';
    const isPastTolerance = scheduledTime.getTime() + toleranceMs < now.getTime() && !isCompleted;
    const isLate = instance.status === 'late' || isPastTolerance;
    const inShift = checkIsInShift(instance.scheduled_for);

    return {
      ...instance,
      isCompleted,
      isPast: isPastTolerance,
      isLate,
      isInShift: inShift
    };
  });

  // ── Task alarms (sound + vibration when a scheduled time is reached) ──
  const alarmTasks = useMemo(
    () =>
      processedInstances
        .filter(i => i.isInShift)
        .map(i => ({
          id: i.id,
          scheduled_for: i.scheduled_for,
          isCompleted: i.isCompleted,
          name: i.task_template?.name || t('taskAlarm.genericTask'),
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskInstances, shiftTemplate, t],
  );

  const { ringing, dismiss } = useTaskAlarms({
    config: shiftTemplate,
    tasks: alarmTasks,
    active: isCheckedInForThisPatient,
    storageKey: `careos_task_alarms_${activeShift?.id ?? 'none'}`,
  });

  const completedCount = processedInstances.filter(t => t.isCompleted).length;
  // Executadas ficam apenas no histórico; a agenda mostra só o que está em aberto.
  const openInstances = processedInstances.filter(t => !t.isCompleted);

  const totalCount = processedInstances.length;
  // Show "-" when not checked in for this patient or no tasks scheduled
  const adherencePercent = !isCheckedInForThisPatient || totalCount === 0
    ? null
    : Math.round((completedCount / totalCount) * 100);

  const getTaskIcon = (type: string) => {
    switch (type) {
      case 'fixed_medication': return <Pill className="w-4 h-4" />;
      case 'vital_sign': return <Activity className="w-4 h-4" />;
      case 'hygiene': return <Droplets className="w-4 h-4" />;
      case 'food': return <Utensils className="w-4 h-4" />;
      case 'physical_activity': return <Dumbbell className="w-4 h-4" />;
      default: return <CheckCircle2 className="w-4 h-4" />;
    }
  };

  const getTaskColor = (type: string) => {
    switch (type) {
      case 'fixed_medication': return 'text-primary';
      case 'vital_sign': return 'text-info';
      case 'hygiene': return 'text-primary';
      case 'food': return 'text-warning';
      case 'physical_activity': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  if (isLoading || shiftLoading || roleLoading) {
    return (
      <AppLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-muted rounded-xl" />
          <div className="h-32 bg-muted rounded-xl" />
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  const dateLocale = i18n.language === 'pt-BR' ? ptBR : enUS;

  if (!canExecuteTasks) {
    return (
      <AppLayout showLogo showNotifications>
        <div className="p-4">
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center">
                  <ShieldX className="w-8 h-8 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('caregiver.accessRestricted')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('caregiver.noPermission')}
                  </p>
                  <Button onClick={() => navigate(`/app/patients/${patientId}`)}>
                    {t('caregiver.backToPatient')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Calculate ad-hoc tasks count from today's executions (sporadic or no task_id)
  const sporadicMedsCount = todayExecutions.filter(e => e.adhoc_task_type === 'sporadic_medication').length;
  const incidentsCount = todayExecutions.filter(e => e.adhoc_task_type === 'incident').length;

  // Calculate late tasks count
  const lateTasksCount = processedInstances.filter(t => t.isLate && !t.isCompleted).length;

  // Calculate alerts count from today's executions


  return (
    <AppLayout
      title={t('caregiver.workspace')}
      showBack
      onBack={() => navigate(`/app/patients/${patientId}`)}
      showFAB={false}
    >
      <TaskAlarmOverlay
        alarm={ringing}
        onDismiss={dismiss}
        onOpenTask={(instanceId) => {
          const instance = processedInstances.find(i => i.id === instanceId);
          dismiss();
          if (instance?.task_template?.id) {
            navigate(`/app/caregiver/${patientId}/execute/${instance.task_template.id}?instanceId=${instanceId}`);
          }
        }}
      />
      <PullToRefresh onRefresh={async () => { await refreshShift(); await fetchData(); }} className="min-h-full">

        <div className="p-4">
          <div className="mb-4" data-tour="caregiver-checkin">
            <PatientDetailsCard
              patient={sanitizePatientData(patient, isOwner || isCaregiver)}
              activeShift={patientActiveShift}
              showCheckIn={isCaregiver && !hasAnyActiveShift && !patientActiveShift}
              showCheckOut={isCaregiver && isCheckedInForThisPatient}
              isCheckedIn={isCheckedInForThisPatient}
              onCheckIn={() => navigate(`/app/patients/${patientId}/check-in`)}
              onCheckOut={() => navigate(`/app/caregiver/${patientId}/handoff`)}
              showHealthDetails={isOwner || isCaregiver}
              showNotes={isOwner || isCaregiver}
              showLocationStatus={isOwner && isCheckedInForThisPatient}
              allowedRadius={systemSettings.allowed_checkin_radius || 500}
              activeShiftInOtherPatient={
                hasAnyActiveShift && !isCheckedInForThisPatient && !patientActiveShift && activeShift
                  ? {
                      patientId: activeShift.patient_id,
                      checkInAt: activeShift.check_in_at,
                    }
                  : null
              }
              onGoToActiveShift={() => {
                if (activeShift) {
                  setFocusedPatientId(activeShift.patient_id);
                  navigate(`/app/caregiver/${activeShift.patient_id}`);
                }
              }}
            />
          </div>

          {/* KPIs - Standardized according to new definitions */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Card className="text-center p-2">
              <div className="text-xl font-bold text-foreground">
                {totalCount}
              </div>
              <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {t('dashboard.plannedTasks')}
              </p>
            </Card>

            <Card className="text-center p-2">
              <div className="text-xl font-bold text-foreground">
                {completedCount}
              </div>
              <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {t('dashboard.executedTasks')}
              </p>
            </Card>

            <Card className="text-center p-2">
              <div className="text-xl font-bold text-foreground">
                {adherencePercent !== null ? `${adherencePercent}%` : '-'}
              </div>
              <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {t('dashboard.completion')}
              </p>
            </Card>

            <Card className="text-center p-2">
              <div className="text-xl font-bold text-foreground">
                {sporadicMedsCount}
              </div>
              <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {t('dashboard.sporadicMeds')}
              </p>
            </Card>

            <Card className="text-center p-2">
              <div className={`text-xl font-bold ${incidentsCount > 0 ? 'text-destructive' : 'text-foreground'}`}>
                {incidentsCount}
              </div>
              <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {t('dashboard.incidents')}
              </p>
            </Card>

            <Card className="text-center p-2">
              <div className={`text-xl font-bold ${lateTasksCount > 0 ? 'text-warning' : 'text-foreground'}`}>
                {lateTasksCount}
              </div>
              <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                {t('dashboard.lateTasks')}
              </p>
            </Card>
          </div>

          {/* Action Buttons */}
          {isCheckedInForThisPatient && (
            <div className="space-y-3 mb-4" data-tour="caregiver-actions">
              <div className="grid grid-cols-2 gap-3">
                <Button
                  className="w-full h-auto min-h-11 py-2 whitespace-normal text-center leading-tight"
                  size="lg"
                  onClick={() => navigate(`/app/caregiver/${patientId}/sporadic`)}
                >
                  <Pill className="w-5 h-5 mr-2 shrink-0" />
                  <span className="whitespace-normal">{t('caregiver.sporadicMed')}</span>
                </Button>

                <Button
                  variant="destructive"
                  className="w-full h-auto min-h-11 py-2 whitespace-normal text-center leading-tight"
                  size="lg"
                  onClick={() => navigate(`/app/caregiver/${patientId}/incident`)}
                >
                  <AlertTriangle className="w-5 h-5 mr-2 shrink-0" />
                  <span className="whitespace-normal">{t('caregiver.incident')}</span>
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="w-full h-auto min-h-11 py-2 whitespace-normal text-center leading-tight"
                  size="lg"
                  onClick={() => navigate(`/app/caregiver/${patientId}/adhoc`)}
                >
                  <Plus className="w-5 h-5 mr-2 shrink-0" />
                  <span className="whitespace-normal">{t('adhocTask.buttonLabel')}</span>
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-auto min-h-11 py-2 whitespace-normal text-center leading-tight"
                  size="lg"
                  onClick={() => navigate(`/app/inventory/${patientId}`)}
                >
                  <Package className="w-5 h-5 mr-2 shrink-0" />
                  <span className="whitespace-normal">{t('inventory.title')}</span>
                </Button>
              </div>
            </div>
          )}

          {/* Timeline */}
          <Card data-tour="caregiver-agenda">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('caregiver.todayAgenda')}</CardTitle>
            </CardHeader>
            <CardContent>
              {openInstances.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {!isCheckedInForThisPatient
                    ? t('caregiver.checkInToSeeTasks')
                    : processedInstances.length === 0
                      ? t('caregiver.noTasksScheduled')
                      : t('caregiver.allTasksDone')}
                </p>
              ) : (
                <div className="space-y-4">
                  {([
                    { key: 'late', label: t('familyDashboard.late'), color: 'text-destructive', items: openInstances.filter(i => i.isLate) },
                    { key: 'pending', label: t('familyDashboard.pending'), color: 'text-warning', items: openInstances.filter(i => !i.isLate) },
                  ] as const).filter(group => group.items.length > 0).map(group => (
                    <div key={group.key}>
                      <h3 className={`text-sm font-medium mb-2 ${group.color}`}>
                        {group.label} ({group.items.length})
                      </h3>
                      <div className="space-y-3">
                        {group.items.map((instance) => {
                          const template = instance.task_template;
                          const scheduledTime = format(parseISO(instance.scheduled_for), 'HH:mm');
                          const isHighlighted = !!highlightId && instance.execution_id === highlightId;

                          return (
                            <AgendaTaskItem
                              key={instance.id}
                              instance={instance}
                              template={template}
                              scheduledTime={scheduledTime}
                              isHighlighted={isHighlighted}
                              isCheckedInForThisPatient={isCheckedInForThisPatient}
                              isInShift={instance.isInShift}
                              hasShiftTemplate={!!shiftTemplate}
                              t={t}
                              getTaskIcon={getTaskIcon}
                              getTaskColor={getTaskColor}
                              onClick={() => {
                                if (isCheckedInForThisPatient && instance.isInShift) {
                                  navigate(`/app/caregiver/${patientId}/execute/${template?.id}?instanceId=${instance.id}`);
                                }
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Patient Feed Section */}
          <div className="mt-6">


            {feedItems.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {t('caregiver.noActivityToday')}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <h2 className="text-base font-semibold ml-1">{t('familyDashboard.executedHistoryTitle')}</h2>
                {groupedEvents.map(group => (

                  <div key={group.date.toISOString()}>
                    <div className="space-y-3">
                      {group.items.map((execution) => (
                        <TaskFeedItem
                          key={execution.id}
                          execution={execution}
                          patientId={patientId}
                          isOwner={isOwner}
                          isHighlighted={execution.id === highlightId}
                          autoOpenComments={openComments && execution.id === highlightId}
                          highlightCommentId={execution.id === highlightId ? highlightCommentId : undefined}
                          isViewerTask={execution.executed_by === user?.id} // Highlight viewer's tasks
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {/* Infinite scroll trigger / Loading state */}
                <div ref={feedEndRef} className="py-4">
                  {isFeedLoading && (
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">{t('common.loading')}</span>
                    </div>
                  )}

                  {!isFeedLoading && hasMoreFeed && (
                    <Button
                      variant="ghost"
                      className="w-full text-muted-foreground hover:text-foreground"
                      onClick={() => loadFeed(feedOffset)}
                    >
                      {t('common.viewAll')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>


        </div>
      </PullToRefresh>

      {/* Guided tour for caregivers on their first shift */}
      {isCaregiver && !roleLoading && (
        <CoachMark
          tourId="caregiverShift"
          steps={[
            {
              target: 'caregiver-checkin',
              titleKey: 'tour.caregiverShift.checkIn.title',
              descKey: 'tour.caregiverShift.checkIn.desc',
            },
            {
              target: 'caregiver-agenda',
              titleKey: 'tour.caregiverShift.agenda.title',
              descKey: 'tour.caregiverShift.agenda.desc',
            },
            {
              target: 'caregiver-actions',
              titleKey: 'tour.caregiverShift.incident.title',
              descKey: 'tour.caregiverShift.incident.desc',
            },
            {
              target: 'caregiver-checkin',
              titleKey: 'tour.caregiverShift.checkOut.title',
              descKey: 'tour.caregiverShift.checkOut.desc',
            },
          ]}
        />
      )}
    </AppLayout>
  );
}
